import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
    type InfiniteData,
    type QueryKey,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo, useState, useCallback } from "react";
import { fetchData, post, patch, del } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { CommandSessionList, CommandSession } from "@/types/command-agent";
import { toast } from "sonner";

const friendlyErrorDescription = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
    if (Array.isArray(data?.detail) && typeof data.detail[0]?.msg === "string") return data.detail[0].msg;
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    return fallback;
};

function sortSessions(sessions: CommandSession[]): CommandSession[] {
    return [...sessions].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const aTime = new Date(a.updated_at ?? a.created_at).getTime();
        const bTime = new Date(b.updated_at ?? b.created_at).getTime();
        return bTime - aTime;
    });
}

function dedupeSessions(raw: CommandSession[]): CommandSession[] {
    const seen = new Set<string>();
    return raw.filter((session) => {
        if (seen.has(session.id)) return false;
        seen.add(session.id);
        return true;
    });
}

type SessionsInfinite = InfiniteData<CommandSessionList>;

const projectSessionsInfiniteKey = (projectId: string) =>
    ["command-project-sessions", projectId, "infinite"] as const;

const projectSessionsPageKey = (projectId: string) =>
    ["command-project-sessions-page", projectId] as const;

function isSessionsInfinite(data: unknown): data is SessionsInfinite {
    return Boolean(
        data &&
            typeof data === "object" &&
            "pages" in data &&
            Array.isArray((data as SessionsInfinite).pages)
    );
}

interface CreateSessionPayload {
    project_id?: string | null;
    /** Skip success toast — used when auto-creating a session on first send. */
    silent?: boolean;
}

function removeSessionFromInfinite(
    data: SessionsInfinite | undefined,
    sessionId: string
): SessionsInfinite | undefined {
    if (!data) return data;
    return {
        ...data,
        pages: data.pages.map((page) => ({
            ...page,
            items: (page.items ?? []).filter((session) => session.id !== sessionId),
        })),
    };
}

function addSessionToInfinite(
    data: SessionsInfinite | undefined,
    session: CommandSession
): SessionsInfinite {
    if (!data?.pages?.length) {
        return {
            pages: [{ items: [session], item_count: 1, page_count: 1 }],
            pageParams: [1],
        };
    }
    const already = data.pages.some((page) =>
        (page.items ?? []).some((item) => item.id === session.id)
    );
    if (already) return data;
    const [first, ...rest] = data.pages;
    return {
        ...data,
        pages: [{ ...first, items: [session, ...(first.items ?? [])] }, ...rest],
    };
}

export function findCachedCommandSession(
    queryClient: ReturnType<typeof useQueryClient>,
    sessionId: string
): CommandSession | undefined {
    for (const [queryKey, data] of queryClient.getQueriesData<SessionsInfinite>({
        queryKey: ["command-sessions"],
    })) {
        const listAgentId = typeof queryKey[1] === "string" ? queryKey[1] : undefined;
        for (const page of data?.pages ?? []) {
            const hit = page.items.find((session) => session.id === sessionId);
            if (hit) {
                return {
                    ...hit,
                    agent_id: hit.agent_id ?? listAgentId ?? null,
                };
            }
        }
    }

    for (const [, data] of queryClient.getQueriesData<SessionsInfinite>({
        queryKey: ["command-project-sessions"],
    })) {
        for (const page of data?.pages ?? []) {
            const hit = page.items.find((session) => session.id === sessionId);
            if (hit) return hit;
        }
    }
    return undefined;
}

/** Prefer stored owner, then cache (incl. query-key agent), then URL agent for cold starts. */
export function resolveCommandSessionAgentId(
    sessionId: string | null | undefined,
    urlAgentId: string | null | undefined,
    options?: {
        cachedSession?: CommandSession;
        storedSessionAgentId?: string | null;
    }
): string | null {
    if (!sessionId) return urlAgentId ?? null;

    const cachedAgentId = options?.cachedSession?.agent_id;
    if (cachedAgentId) return cachedAgentId;

    if (options?.storedSessionAgentId) return options.storedSessionAgentId;

    // Session not cached yet — trust URL agent (direct link / first paint).
    if (!options?.cachedSession && urlAgentId) return urlAgentId;

    return urlAgentId ?? null;
}

/** Subscribes to session list caches so cross-agent project sessions resolve `agent_id`. */
export function useCachedCommandSession(sessionId: string | null | undefined): CommandSession | undefined {
    const queryClient = useQueryClient();
    const readSession = useCallback(() => {
        if (!sessionId) return undefined;
        return findCachedCommandSession(queryClient, sessionId);
    }, [queryClient, sessionId]);

    const [session, setSession] = useState<CommandSession | undefined>(() => readSession());

    useEffect(() => {
        setSession(readSession());
        if (!sessionId) return;

        return queryClient.getQueryCache().subscribe((event) => {
            if (event.type !== "updated") return;
            const root = event.query.queryKey[0];
            if (root !== "command-sessions" && root !== "command-project-sessions") return;
            const next = readSession();
            setSession((prev) =>
                prev?.id === next?.id &&
                prev?.agent_id === next?.agent_id &&
                prev?.project_id === next?.project_id
                    ? prev
                    : next
            );
        });
    }, [sessionId, queryClient, readSession]);

    return session;
}

async function refreshSessionLists(
    queryClient: ReturnType<typeof useQueryClient>,
    agentId: string | null
) {
    await Promise.all([
        queryClient.refetchQueries({ queryKey: ["command-sessions", agentId] }),
        queryClient.refetchQueries({ queryKey: ["command-project-sessions"] }),
        queryClient.refetchQueries({ queryKey: ["command-project-sessions-page"] }),
        queryClient.invalidateQueries({ queryKey: ["command-projects"] }),
    ]);
}

/**
 * Agent sessions list.
 * - No `projectId` → unfiled only (Other chats).
 * - With `projectId` → that project's sessions via `?project_id=`.
 */
export function useCommandSessions(
    agentId: string | null,
    options?: { search?: string; projectId?: string | null; enabled?: boolean }
) {
    const queryClient = useQueryClient();
    const search = options?.search?.trim() || "";
    const projectId = options?.projectId ?? null;
    const enabled = options?.enabled !== false;
    const scopeKey = projectId ?? "unfiled";
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const { hasPermission: canListSessions } = useProtectedQuery({
        queryKey: ["_gated_list_command_sessions"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_SESSIONS"],
        enabled: false,
    });

    const {
        data: sessionsData,
        isLoading: isLoadingSessions,
        isFetching: isFetchingSessions,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<CommandSessionList>({
        queryKey: ["command-sessions", agentId, search, scopeKey],
        initialPageParam: 1,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: "50",
                sort_by: "updated_at",
                sort_order: "desc",
            });
            if (search) params.set("search", search);
            if (projectId) params.set("project_id", projectId);
            return fetchData(
                `/api/command-team/agents/${agentId}/sessions?${params.toString()}`
            );
        },
        getNextPageParam: (lastPage, pages) => {
            if (pages.length < lastPage.page_count) {
                return pages.length + 1;
            }
            return undefined;
        },
        enabled: enabled && canListSessions && Boolean(agentId),
    });

    const allSessions = useMemo(() => {
        if (!sessionsData?.pages) return [];
        return sortSessions(
            dedupeSessions(sessionsData.pages.flatMap((page) => page.items))
        );
    }, [sessionsData]);

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const createSessionMutation = useMutation({
        mutationFn: async (payload?: CreateSessionPayload) => {
            const body: { project_id?: string } = {};
            if (payload?.project_id) body.project_id = payload.project_id;
            const response = await post(`/api/command-team/agents/${agentId}/sessions`, body);
            return response.data as CommandSession;
        },
        onSuccess: (session, variables) => {
            if (agentId && session?.id) {
                const projectId = variables?.project_id ?? null;
                const scopedKey = projectId ?? "unfiled";
                const normalized: CommandSession = {
                    ...session,
                    agent_id: session.agent_id ?? agentId,
                };
                queryClient.setQueriesData<SessionsInfinite>(
                    {
                        queryKey: ["command-sessions", agentId],
                        predicate: (query) => query.queryKey[3] === scopedKey,
                    },
                    (old) => addSessionToInfinite(old, normalized)
                );
                if (projectId) {
                    queryClient.setQueryData<SessionsInfinite>(
                        projectSessionsInfiniteKey(projectId),
                        (old) => addSessionToInfinite(old, normalized)
                    );
                }
            }
            if (!variables?.silent) {
                toast.success("New session created");
            }
            void refreshSessionLists(queryClient, agentId);
        },
        onError: (error: any) => {
            toast.error("Failed to create session", {
                description: friendlyErrorDescription(error, "We couldn't start a new chat. Please try again."),
            });
        },
    });

    const updateSessionMutation = useMutation({
        mutationFn: async (args: {
            sessionId: string;
            pinned?: boolean;
            title?: string;
            project_id?: string | null;
            /** When the session belongs to another agent (e.g. project folder), PATCH under that agent. */
            ownerAgentId?: string | null;
        }) => {
            const targetAgentId =
                args.ownerAgentId ??
                findCachedCommandSession(queryClient, args.sessionId)?.agent_id ??
                agentId;
            if (!targetAgentId) {
                throw new Error("Agent ID is required to update session");
            }
            const payload: { pinned?: boolean; title?: string; project_id?: string | null } = {};
            if (args.pinned !== undefined) payload.pinned = args.pinned;
            if (args.title !== undefined) payload.title = args.title;
            if ("project_id" in args) payload.project_id = args.project_id ?? null;
            const response = await patch(
                `/api/command-team/agents/${targetAgentId}/sessions/${args.sessionId}`,
                payload
            );
            return response.data as CommandSession;
        },
        onMutate: async (args) => {
            const targetAgentId =
                args.ownerAgentId ??
                findCachedCommandSession(queryClient, args.sessionId)?.agent_id ??
                agentId;
            if (!("project_id" in args) || !targetAgentId) return;

            await queryClient.cancelQueries({ queryKey: ["command-sessions", targetAgentId] });
            await queryClient.cancelQueries({ queryKey: ["command-project-sessions"] });

            const previousSessions = queryClient.getQueriesData<SessionsInfinite>({
                queryKey: ["command-sessions", targetAgentId],
            });
            const previousProjectSessions = queryClient.getQueriesData<SessionsInfinite>({
                queryKey: ["command-project-sessions"],
            });

            const cached = findCachedCommandSession(queryClient, args.sessionId);
            const moved: CommandSession = {
                created_at: cached?.created_at ?? new Date().toISOString(),
                updated_at: new Date().toISOString(),
                id: args.sessionId,
                title: cached?.title ?? "Untitled",
                status: cached?.status ?? "IDLE",
                pinned: cached?.pinned ?? false,
                message_count: cached?.message_count ?? 0,
                total_tokens_used: cached?.total_tokens_used ?? 0,
                agent_id: cached?.agent_id ?? targetAgentId,
                project_id: args.project_id ?? null,
            };

            // Drop from every list first
            queryClient.setQueriesData<SessionsInfinite>(
                { queryKey: ["command-sessions", targetAgentId] },
                (old) => removeSessionFromInfinite(old, args.sessionId)
            );
            queryClient.setQueriesData<SessionsInfinite>(
                { queryKey: ["command-project-sessions"] },
                (old) => (isSessionsInfinite(old) ? removeSessionFromInfinite(old, args.sessionId) : old)
            );

            // Insert into destination
            if (args.project_id) {
                queryClient.setQueryData<SessionsInfinite>(
                    projectSessionsInfiniteKey(args.project_id),
                    (old) => addSessionToInfinite(old, moved)
                );
            } else {
                queryClient.setQueriesData<SessionsInfinite>(
                    {
                        queryKey: ["command-sessions", targetAgentId],
                        predicate: (query) => query.queryKey[3] === "unfiled",
                    },
                    (old) => addSessionToInfinite(old, moved)
                );
            }

            return { previousSessions, previousProjectSessions };
        },
        onError: (error: any, args, context) => {
            context?.previousSessions?.forEach(([key, data]) => {
                queryClient.setQueryData(key as QueryKey, data);
            });
            context?.previousProjectSessions?.forEach(([key, data]) => {
                queryClient.setQueryData(key as QueryKey, data);
            });
            toast.error("Failed to update session", {
                description: friendlyErrorDescription(error, "We couldn't update this chat. Please try again."),
            });
        },
        onSettled: async (_data, _error, args) => {
            const targetAgentId =
                args.ownerAgentId ??
                findCachedCommandSession(queryClient, args.sessionId)?.agent_id ??
                agentId;
            if ("project_id" in args) {
                // Force Other chats + every project folder list to refetch
                await refreshSessionLists(queryClient, targetAgentId);
                return;
            }
            await queryClient.invalidateQueries({ queryKey: ["command-sessions", targetAgentId] });
        },
    });

    const deleteSessionMutation = useMutation({
        mutationFn: async (args: string | { sessionId: string; ownerAgentId?: string | null }) => {
            const sessionId = typeof args === "string" ? args : args.sessionId;
            const targetAgentId =
                (typeof args === "object" ? args.ownerAgentId : undefined) ??
                findCachedCommandSession(queryClient, sessionId)?.agent_id ??
                agentId;
            if (!targetAgentId) {
                throw new Error("Agent ID is required to delete session");
            }
            const response = await del(`/api/command-team/agents/${targetAgentId}/sessions/${sessionId}`);
            return response.data;
        },
        onSuccess: async () => {
            await refreshSessionLists(queryClient, agentId);
            toast.success("Session deleted successfully");
        },
        onError: (error: any) => {
            toast.error("Failed to delete session", {
                description: friendlyErrorDescription(error, "We couldn't delete this chat. Please try again."),
            });
        },
    });

    return {
        allSessions,
        isLoadingSessions,
        isFetchingSessions,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        loadMoreRef,
        canListSessions,
        createSession: createSessionMutation.mutate,
        isCreatingSession: createSessionMutation.isPending,
        creatingSessionProjectId: createSessionMutation.isPending
            ? createSessionMutation.variables?.project_id ?? null
            : null,
        updateSession: updateSessionMutation.mutate,
        isUpdatingSession: updateSessionMutation.isPending,
        deleteSession: deleteSessionMutation.mutate,
        isDeletingSession: deleteSessionMutation.isPending,
    };
}

/**
 * Sessions in a project via GET /projects/{id}/sessions.
 * Same set as GET /agents/{id}/sessions?project_id=X.
 */
export function useCommandProjectSessions(projectId: string | null) {
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const { hasPermission: canListSessions } = useProtectedQuery({
        queryKey: ["_gated_list_command_sessions"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_SESSIONS"],
        enabled: false,
    });

    const {
        data: sessionsData,
        isLoading: isLoadingSessions,
        isFetching: isFetchingSessions,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<CommandSessionList>({
        queryKey: projectSessionsInfiniteKey(projectId ?? ""),
        initialPageParam: 1,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: "50",
                sort_by: "updated_at",
                sort_order: "desc",
            });
            return fetchData(
                `/api/command-team/projects/${projectId}/sessions?${params.toString()}`
            );
        },
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage || !Array.isArray(pages)) return undefined;
            const pageCount = lastPage.page_count ?? 1;
            if (pages.length < pageCount) return pages.length + 1;
            return undefined;
        },
        enabled: canListSessions && Boolean(projectId),
    });

    const sessions = useMemo(() => {
        const pages = sessionsData?.pages;
        if (!Array.isArray(pages)) return [];
        return sortSessions(
            dedupeSessions(pages.flatMap((page) => page?.items ?? []))
        );
    }, [sessionsData]);

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        sessions,
        isLoadingSessions,
        isFetchingSessions,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
        canListSessions,
    };
}
